import { compact } from 'lodash';
import { useMemo } from 'react';

import {
   MedicalDataIcon,
   NotesIcon,
   SidePanel,
   SidePanelTab,
   StethoscopeIcon,
   TaskIcon,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { VisitDiagnoses } from '~libs.shared.visit';
import { AiAssistantIcon, AiAssistantSidePanelContentSlot } from '~libs.desktop.ai-assistant';

import { useRedactionContext, useVisitFormContext } from '../common/visitForm/VisitFormContext';
import {
   aiAssistantSidePanelTranslationKeys,
   historicalVisitsTranslationKeys,
   medicalDataTranslationKeys,
   notesTranslationKeys,
   patientConsentsAndAuthorizationsTranslationKeys,
} from '../translations';

import {
   HistoricalVisitsSidePanelContentSlot,
   HistoricalVisitsSidePanelContentSlotType,
} from './historicalVisits/HistoricalVisitsSidePanelContentSlot';
import { MedicalDataSidePanelContentSlot } from './medicalData/MedicalDataSidePanelContentSlot';
import { UnsignedMedicalInformationAttachmentBadge } from './medicalData/UnsignedMedicalInformationAttachmentBadge';
import { NotesSidePanelContentSlot } from './notes/NotesSidePanelContentSlot';
import { PatientConsentsAndAuthorizationsSidePanelContentSlot } from './patientConsentsAndAuthorizations/PatientConsentsAndAuthorizationsSidePanelContentSlot';
import { VisitSidePanelTabIdUnion, VisitSidePanelTabs } from './visitSidePanel.types';

type VisitSidePanelTabOptions = {
   patientId: number;
};

const useVisitSidePanelTabs = ({ patientId }: VisitSidePanelTabOptions): SidePanelTab<VisitSidePanelTabIdUnion>[] => {
   return useMemo(
      () =>
         compact([
            {
               label: medicalDataTranslationKeys.medicalData,

               icon: ({ active }) => (
                  <UnsignedMedicalInformationAttachmentBadge patientId={patientId} visible={!active}>
                     <MedicalDataIcon />
                  </UnsignedMedicalInformationAttachmentBadge>
               ),
               id: VisitSidePanelTabs.MEDICAL_DATA,
            },
            {
               label: historicalVisitsTranslationKeys.visitsHistory,
               icon: <StethoscopeIcon />,
               id: VisitSidePanelTabs.VISITS_HISTORY,
            },
            protectFeature('creatingVisits', {
               label: notesTranslationKeys.notes,
               icon: <NotesIcon />,
               id: VisitSidePanelTabs.NOTES,
            }),
            {
               label: patientConsentsAndAuthorizationsTranslationKeys.consents,
               icon: <TaskIcon />,
               id: VisitSidePanelTabs.CONSENTS,
            },
            // eslint-disable-next-line @dreryk/check-experimental-to-do
            protectFeature('experimental', {
               label: aiAssistantSidePanelTranslationKeys.tabLabel,
               icon: <AiAssistantIcon />,
               id: VisitSidePanelTabs.AI_ASSISTANT,
            }),
         ]),
      [patientId]
   );
};

type VisitSidePanelProps = {
   readonly patientId: number;
   readonly currentVisitId: number;
   readonly visitDiagnoses: VisitDiagnoses;
};

// add other type to union below, when needed
type ContentComponentType = HistoricalVisitsSidePanelContentSlotType;
const getSidePanelContentComponent = (
   activeTabId: VisitSidePanelTabIdUnion,
   creatingPatientNotesAvailable: boolean
): ContentComponentType | null => {
   switch (activeTabId) {
      case VisitSidePanelTabs.VISITS_HISTORY:
         return HistoricalVisitsSidePanelContentSlot;
      case VisitSidePanelTabs.MEDICAL_DATA:
         return MedicalDataSidePanelContentSlot;
      case VisitSidePanelTabs.NOTES:
         return creatingPatientNotesAvailable ? NotesSidePanelContentSlot : null;
      case VisitSidePanelTabs.CONSENTS:
         return PatientConsentsAndAuthorizationsSidePanelContentSlot;
      default:
         return null;
   }
};

export const VisitSidePanel = (props: VisitSidePanelProps): JSX.Element => {
   const creatingPatientNotesAvailable = protectFeature('creatingPatientNotes');
   const { appendToField, getFieldHtml, setFieldHtml } = useVisitFormContext();
   const { setLockedField } = useRedactionContext();

   return (
      <SidePanel
         testId={'visitSidePanel'}
         id={'visitSidePanel'}
         tabs={useVisitSidePanelTabs({
            patientId: props.patientId,
         })}
      >
         {({ activeTabId }) => {
            if (activeTabId === VisitSidePanelTabs.AI_ASSISTANT) {
               return (
                  <AiAssistantSidePanelContentSlot
                     onAppendToField={appendToField}
                     getFieldHtml={getFieldHtml}
                     setFieldHtml={setFieldHtml}
                     setLockedField={setLockedField}
                  />
               );
            }
            const ContentComponent = getSidePanelContentComponent(activeTabId, creatingPatientNotesAvailable);
            if (!ContentComponent) {
               return null;
            }

            return (
               <ContentComponent
                  patientId={props.patientId}
                  currentVisitId={props.currentVisitId}
                  visitDiagnoses={props.visitDiagnoses}
               />
            );
         }}
      </SidePanel>
   );
};
